import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const OverallSummary = ({ analyticData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);

  // Get the overall summary from the analytics data
  const overallSummary =
    analyticData?.data?.issueAnalytics?.result?.answer || '';

  // Function to get truncated text (first 3 lines or ~150 characters)
  const getTruncatedSummary = (text) => {
    if (!text) return '';

    // Split by lines and take first 3, or truncate at ~150 characters
    const lines = text.split('\n');
    const firstThreeLines = lines.slice(0, 3).join('\n');

    if (firstThreeLines.length <= 150) {
      return firstThreeLines;
    }

    // If still too long, truncate at 150 characters and add ellipsis
    return text.substring(0, 150) + '...';
  };

  const truncatedSummary = getTruncatedSummary(overallSummary);
  const shouldShowToggle = overallSummary.length > truncatedSummary.length;

  // Copy to clipboard function
  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(overallSummary);
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = overallSummary;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 2000);
    }
  };

  if (!overallSummary || overallSummary.trim() === '') {
    return null; // Don't render if no summary available
  }

  return (
    <div style={{ padding: '0 16px', marginTop: '24px' }}>
      <Accordion
        expanded={isExpanded}
        onChange={(event, expanded) => setIsExpanded(expanded)}
        style={{
          border: '1px solid #d1ecf1',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
        }}>
        <AccordionSummary
          expandIcon={shouldShowToggle ? <ExpandMoreIcon /> : null}
          aria-controls="overall-summary-content"
          id="overall-summary-header"
          style={{
            backgroundColor: '#e7f3ff',
            borderRadius: '8px 8px 0 0',
            cursor: shouldShowToggle ? 'pointer' : 'default',
          }}>
          <div style={{ fontWeight: 'bold', color: '#0366d6' }}>
            総合診断サマリー
          </div>
        </AccordionSummary>
        <AccordionDetails style={{ padding: '16px', position: 'relative' }}>
          <div
            style={{
              lineHeight: '1.6',
              fontSize: '0.95rem',
              color: '#333333',
              whiteSpace: 'pre-wrap', // Preserve line breaks
              paddingRight: '40px', // Make space for copy button
            }}>
            {isExpanded ? overallSummary : truncatedSummary}
            {!isExpanded && shouldShowToggle && (
              <span style={{ color: '#666', fontStyle: 'italic' }}>
                {' '}
                （クリックして全文を表示）
              </span>
            )}
          </div>

          {/* Copy button positioned at bottom right */}
          <Tooltip
            title={copyTooltipOpen ? 'コピーしました！' : 'コピー'}
            open={copyTooltipOpen || undefined}
            arrow
            placement="top">
            <IconButton
              onClick={handleCopyToClipboard}
              size="small"
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid #ddd',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                },
              }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

OverallSummary.propTypes = {
  analyticData: PropTypes.shape({
    data: PropTypes.shape({
      issueAnalytics: PropTypes.shape({
        result: PropTypes.shape({
          answer: PropTypes.string,
        }),
      }),
    }),
  }),
};

export default OverallSummary;
